/**
 * Iter 67aq — Compact "perks" badge strip rendered on car cards across
 * Home, Browse, and the public car-detail page.
 *
 * Surfaces five quick decision-aiding signals in a single line of color-coded
 * pills so renters can compare cars at a glance without opening detail pages:
 *
 *   1. Luggage capacity        (vendor-set `luggage_capacity` field)
 *   2. Meet & Greet (airport)   (auto-derived: any pickup_location contains "airport")
 *   3. Unlimited Km             (derived: car.mileage_policy === "unlimited")
 *   4. Free cancellation 48h    (platform-wide commitment)
 *   5. Accepted payment methods (existing pickup_payment_methods_resolved)
 *
 * Each badge self-hides when its data isn't applicable, so cards don't end up
 * with empty/placeholder pills. Payment methods render as icon-only chips
 * (compact mode) so the strip stays visually balanced even when the host
 * accepts 4–5 different methods.
 */
import {
  Briefcase, Plane, Infinity as InfinityIcon, ShieldCheck, Ban,
  Banknote, CreditCard, Wallet, Link as LinkIcon, Landmark, Smartphone,
} from "lucide-react";

const PAYMENT_ICONS = {
  cash:           Banknote,
  card_on_pickup: CreditCard,
  paypal:         Wallet,
  stripe:         LinkIcon,
  bank_transfer:  Landmark,
  revolut:        Smartphone,
};

const PAYMENT_TONES = {
  cash:           "bg-emerald-50 text-emerald-700 ring-emerald-200",
  card_on_pickup: "bg-blue-50 text-blue-700 ring-blue-200",
  paypal:         "bg-sky-50 text-sky-700 ring-sky-200",
  stripe:         "bg-violet-50 text-violet-700 ring-violet-200",
  bank_transfer:  "bg-teal-50 text-teal-700 ring-teal-200",
  revolut:        "bg-neutral-100 text-neutral-800 ring-neutral-300",
};

function _isAirportPickup(car) {
  const list = car?.pickup_locations || [];
  return list.some((p) => typeof p === "string" && /\bairport\b/i.test(p));
}

/**
 * Iter 67aw — turns an hours value into a renter-friendly chip label:
 *   24  → "24h"
 *   48  → "48h"
 *   72  → "72h"
 *   168 → "7 days"
 *   336 → "14 days"
 *   0   → null  (suppress)
 * Anything ≥ 72 and an exact multiple of 24 renders as "N days"; otherwise
 * we fall back to the hours form so vendors can pick odd windows (e.g. 36h).
 */
function _formatCancelWindow(hours) {
  if (!hours || hours <= 0) return null;
  // 24h, 48h are how the rental industry phrases short windows — keep them in
  // hours. Anything ≥ 72 and a clean multiple of 24 reads better as "N days".
  if (hours >= 72 && hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? "1 day" : `${days} days`;
  }
  return `${hours}h`;
}

export default function CarCardBadges({
  car,
  className = "",
  // Free-cancellation hours fallback when the car doesn't have its own
  // `free_cancellation_hours` set. Set to null to suppress the chip entirely
  // even when no per-car override exists.
  freeCancellationHours = 48,
  // Whether to render payment-method icon row inline. False keeps the strip
  // tight on small grid cards (Home), true on horizontal Browse cards.
  showPayments = true,
  testIdPrefix = "carcard-perks",
}) {
  if (!car) return null;
  const luggage = Number(car.luggage_capacity) || 0;
  const isAirport = _isAirportPickup(car);
  const isUnlimited = car.mileage_policy === "unlimited";
  const methods = Array.isArray(car.pickup_payment_methods_resolved)
    ? car.pickup_payment_methods_resolved
    : [];

  // Iter 67aw — per-car override takes precedence over the platform default.
  // null/undefined ⇒ use prop default. 0 (number) ⇒ explicitly suppressed.
  const _carCancel = car.free_cancellation_hours;
  const cancelHours = (_carCancel === null || _carCancel === undefined)
    ? freeCancellationHours
    : Number(_carCancel);
  const cancelLabel = _formatCancelWindow(cancelHours);

  // Cap payment icons at 4 so the strip never spills onto a second line.
  const paymentIcons = showPayments ? methods.slice(0, 4) : [];
  const overflow = showPayments && methods.length > 4 ? methods.length - 4 : 0;

  // Iter 69 — vendor-toggled "No credit card required" perk.
  const noCreditCard = !!car.no_credit_card_required;

  const anyPerk = luggage > 0 || isAirport || isUnlimited || !!cancelLabel || noCreditCard;
  if (!anyPerk && paymentIcons.length === 0) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid={testIdPrefix}
    >
      {luggage > 0 && (
        <span
          className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 ring-1 ring-amber-200 rounded-full text-[10px] font-semibold px-2 py-0.5"
          title={`${luggage} suitcase${luggage === 1 ? "" : "s"} fit in the boot`}
          data-testid={`${testIdPrefix}-luggage`}
        >
          <Briefcase className="h-3 w-3" /> {luggage}
        </span>
      )}
      {isAirport && (
        <span
          className="inline-flex items-center gap-1 bg-sky-50 text-sky-800 ring-1 ring-sky-200 rounded-full text-[10px] font-semibold px-2 py-0.5"
          title={
            Number(car.meet_greet_fee) > 0
              ? `Meet & Greet at the airport · €${Number(car.meet_greet_fee).toFixed(0)} flat fee`
              : "Meet & greet at the airport"
          }
          data-testid={`${testIdPrefix}-meet-greet`}
        >
          <Plane className="h-3 w-3" />
          {Number(car.meet_greet_fee) > 0
            ? <>Meet & Greet · €{Number(car.meet_greet_fee).toFixed(0)}</>
            : "Meet & Greet"}
        </span>
      )}
      {isUnlimited && (
        <span
          className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 rounded-full text-[10px] font-semibold px-2 py-0.5"
          title="Unlimited kilometres included"
          data-testid={`${testIdPrefix}-unlimited-km`}
        >
          <InfinityIcon className="h-3 w-3" /> Unlimited km
        </span>
      )}
      {/* Iter 74k — user requested "No credit card" chip to sit BETWEEN
          Unlimited km and Free cancellation for the most-scanned trust order. */}
      {noCreditCard && (
        <span
          className="inline-flex items-center gap-1 bg-rose-50 text-rose-800 ring-1 ring-rose-200 rounded-full text-[10px] font-semibold px-2 py-0.5"
          title="No credit card required — debit card or other accepted methods are fine"
          data-testid={`${testIdPrefix}-no-credit-card`}
        >
          <Ban className="h-3 w-3" /> No credit card
        </span>
      )}
      {cancelLabel && (
        <span
          className="inline-flex items-center gap-1 bg-violet-50 text-violet-800 ring-1 ring-violet-200 rounded-full text-[10px] font-semibold px-2 py-0.5"
          title={`Free cancellation up to ${cancelLabel} before pickup`}
          data-testid={`${testIdPrefix}-free-cancellation`}
        >
          <ShieldCheck className="h-3 w-3" /> Free cancellation {cancelLabel}
        </span>
      )}
      {paymentIcons.length > 0 && (
        <span
          className="inline-flex items-center gap-1 ml-0.5"
          data-testid={`${testIdPrefix}-payments`}
        >
          {paymentIcons.map((m) => {
            const Icon = PAYMENT_ICONS[m.key] || CreditCard;
            const tone = PAYMENT_TONES[m.key] || PAYMENT_TONES.card_on_pickup;
            return (
              <span
                key={m.key}
                className={`inline-flex items-center justify-center h-5 w-5 rounded-full ring-1 ${tone}`}
                title={`Accepted at pickup · ${m.label}`}
                data-testid={`${testIdPrefix}-payment-${m.key}`}
              >
                <Icon className="h-2.5 w-2.5" />
              </span>
            );
          })}
          {overflow > 0 && (
            <span
              className="inline-flex items-center justify-center h-5 px-1.5 rounded-full bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200 text-[9px] font-bold"
              title="More accepted payment methods"
            >
              +{overflow}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
